import React, { useEffect } from 'react';
import PropTypes from 'prop-types';

import { t } from '../../../../services/i18nService';
import styles from './matchInteraction.scss';

import LoadTracker from '../../../../services/LoadTracker';
import MatchInteractionRow from './MatchInteractionRow';
import MatchPaperBased from './MatchPaperBased';

import matchInteractionSelector from '../../../../selectors/question/interactions/matchInteraction/MatchInteractionSelector';
import scorableItemNumberingService from '../../../../services/questionView/ScorableItemNumberingService';
import Prompt from '../Prompt';
import InteractionNumber from './InteractionNumber';
import QuestionDisplayFlag from '../../itemFlag/QuestionDisplayFlag';

const MatchInteractionPure = (props) => {
    const {
        callback,
        correctResponses,
        direction,
        flaggedItems,
        handleInteractionReady,
        isInverted,
        isPaperBased,
        isReadOnly,
        matchColumns,
        matchRows,
        model,
        questionModel,
        userResponse,
        currentScorableItem,
        showHorizontalFlagging,
        viewMedia,
    } = props;

    useEffect(() => {
        const { objects } = model;
        const imageTracker = new LoadTracker((success) => {
            handleInteractionReady(success);
        });

        if (objects && objects.length) {
            objects.forEach((obj) => {
                const imgElm = document.getElementById(obj['inspera:logicalName']);
                const reportReady = imageTracker.addItemToTrack(`Media ${obj.url || obj.data || obj.logicalName}`);
                imgElm.addEventListener('load', () => {
                    reportReady(true);
                });
                imgElm.addEventListener('error', () => {
                    reportReady(false);
                });
            });
        }

        imageTracker.setAllItemsAdded();
    }, []);

    const handleUserResponse = (event) => {
        const value = event.currentTarget.value;
        const valueIndex = isInverted ? 1 : 0;
        const choiceIdentifier = value.split(' ')[valueIndex];
        const responseIndex = userResponse.findIndex(pair => (pair.split(' ')[valueIndex] === choiceIdentifier));

        let newResponse = [];
        if (responseIndex >= 0) {
            newResponse = [...userResponse];
            newResponse[responseIndex] = value;
        } else {
            newResponse = [...userResponse, value];
        }

        callback(model.identifier, newResponse);
        scorableItemNumberingService.navigateToScorableItem(questionModel.contentRevisionId, model.identifier, choiceIdentifier, true);
    };

    const classList = `${styles.matchInteraction} interaction`;
    if (isPaperBased) {
        return (
            <div className={classList}>
                <Prompt model={model.prompt} />
                <MatchPaperBased {...props} />
            </div>
        );
    }

    const wrapperClassList = [styles.tableWrapper];
    if (showHorizontalFlagging) {
        wrapperClassList.push('contains-horizontal-flagging');
    }

    return (
        <div className={classList}>
            <Prompt model={model.prompt} />
            <div className={wrapperClassList.join(' ')} dir={direction} role="radiogroup">
                <table aria-label={t('interactions.matching.tableAriaLabel')}>
                    <thead>
                        <tr>
                            <th />
                            { matchColumns.map((columnChoice) => {
                                return (<th
                                    id={`columnChoice_${columnChoice.identifier}`}
                                    key={`columnChoice_${columnChoice.identifier}`}
                                >
                                    { isInverted &&
                                        <InteractionNumber
                                            modelIdentifier={model.identifier}
                                            questionContentRevisionId={questionModel.contentRevisionId}
                                            flaggedItems={flaggedItems}
                                            choiceIdentifier={columnChoice.identifier}
                                        />
                                    }

                                    <div dangerouslySetInnerHTML={{ __html: columnChoice.content }} />
                                </th>);
                            })}
                        </tr>
                    </thead>

                    <tbody>
                        { matchRows.map(rowChoice => (
                            <MatchInteractionRow
                                matchColumns={matchColumns}
                                correctResponses={correctResponses}
                                flaggedItems={flaggedItems}
                                handleUserResponse={handleUserResponse}
                                isInverted={isInverted}
                                isReadOnly={isReadOnly}
                                key={rowChoice.identifier}
                                modelIdentifier={model.identifier}
                                questionModel={questionModel}
                                rowChoice={rowChoice}
                                userResponse={userResponse}
                            />
                        ))}
                    </tbody>
                </table>

                { showHorizontalFlagging &&
                    <div className="horizontal-flagging">
                        <QuestionDisplayFlag
                            currentScorableItem={currentScorableItem}
                            isHorizontal
                            question={questionModel}
                            responseIdentifier={model.identifier}
                            showFlagging
                            viewMedia={viewMedia}
                        />
                    </div>
                }

            </div>
        </div>
    );
};

MatchInteractionPure.propTypes = {
    callback: PropTypes.func.isRequired, // callback(responseIdentifier, [simpleChoice identifiers])
    correctResponses: PropTypes.array, // array with correct response
    currentScorableItem: PropTypes.number,
    direction: PropTypes.string,
    flaggedItems: PropTypes.array,
    handleInteractionReady: PropTypes.func.isRequired,
    isInverted: PropTypes.bool.isRequired,
    isPaperBased: PropTypes.bool.isRequired,
    isReadOnly: PropTypes.bool,
    matchColumns: PropTypes.array.isRequired,
    matchRows: PropTypes.array.isRequired,
    model: PropTypes.object.isRequired, // model taken from responseModels related to this interaction
    questionModel: PropTypes.object,
    showHorizontalFlagging: PropTypes.bool.isRequired,
    userResponse: PropTypes.array.isRequired, // array with previous pairs
    viewMedia: PropTypes.string.isRequired,
};

const MatchInteraction = props => (
    <MatchInteractionPure {...props} {...matchInteractionSelector.selectProps(props)} />
);

export default MatchInteraction;
